import React, { useEffect, useRef, useState } from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import SearchInputField from './SearchInputField';
import { navigate } from 'gatsby';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { IExperimentsQueryFilterParams, Order, Status } from './types';
import {
    createExperimentUrlWithNewParams,
    experimentFromIndex,
    getExperimentFiltersFromQueryParams,
    statusToState,
} from './utils';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { useListExperimentsQuery } from '../../apiHooks/experimentApi';
import { IExperiment } from '../../apiHooks/experimentApi.types';
import ExperimentRow from './ExperimentRow';
import ErrorBanner from '@components/atoms/ErrorBanner';
import { parseApiErrorMessage } from '../../apiHooks/utils';
import StatusFilter from './StatusFilter';
import PageLayout from '@components/layouts/PageLayout';

const PAGE_SIZE = 100;

const tableHeaderStyle = 'text-sm font-bold text-gray-900 content-center';

const BIOLIB_EXPERIMENTS_TABLE_REFETCH = 'BIOLIB_EXPERIMENTS_TABLE_REFETCH';

export function dispatchExperimentsTableRefetch() {
    window.dispatchEvent(new Event(BIOLIB_EXPERIMENTS_TABLE_REFETCH));
}

export default function ExperimentsTable() {
    const [page, setPage] = useState<number>(1);
    const [pageMap, setPageMap] = useState<Record<number, IExperiment[]>>({});
    const [createdBefore, setCreatedBefore] = useState<string>(new Date().toISOString());
    const listDivRef = useRef<HTMLDivElement | null>(null);

    const { search, status, ordering } = getExperimentFiltersFromQueryParams();
    const {
        data: resultsData,
        isFetching,
        isLoading,
        refetch,
        error,
    } = useListExperimentsQuery({
        page: page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        state: statusToState[status],
        ordering: ordering ? ordering.replace('started_at', 'created_at') : undefined,
        created_before: createdBefore,
    });
    const itemCountForInfiniteLoader = isLoading ? PAGE_SIZE : (resultsData?.object_count ?? 0);
    const isItemLoaded = (index: number) => experimentFromIndex(pageMap, index, PAGE_SIZE) !== null;

    const handleQueryParamsUpdate = (newParams: Partial<IExperimentsQueryFilterParams>) => {
        const newUrl = createExperimentUrlWithNewParams(newParams);
        if (newUrl !== '') {
            setPageMap({});
            setPage(1);
            setCreatedBefore(new Date().toISOString());
            if ('scrollTo' in listDivRef.current) {
                listDivRef.current.scrollTo({ top: 0, behavior: 'auto' });
            }
            navigate(newUrl);
        }
    };

    const loadMoreItems = async (startIndex: number) => {
        const pageIndex = Math.floor(startIndex / PAGE_SIZE);
        if (!(pageIndex + 1 in pageMap)) {
            if (!isFetching) {
                setPage(pageIndex + 1);
            }
        }
    };

    useEffect(() => {
        if (resultsData?.results) {
            setPageMap((oldPageMap) => ({
                ...oldPageMap,
                [page - 1]: resultsData.results,
            }));
        }
    }, [resultsData]);

    useEffect(() => {
        const handleResultsTabClick = () => {
            setPageMap({});
            setPage(1);
            setCreatedBefore(new Date().toISOString());
            if (listDivRef.current && 'scrollTo' in listDivRef.current) {
                listDivRef.current.scrollTo({ top: 0, behavior: 'auto' });
            }
            refetch();
        };
        window.addEventListener(BIOLIB_EXPERIMENTS_TABLE_REFETCH, handleResultsTabClick);
        return () => {
            window.removeEventListener(BIOLIB_EXPERIMENTS_TABLE_REFETCH, handleResultsTabClick);
        };
    }, [refetch]);

    return (
        <PageLayout>
            <div className='flex-1 relative flex'>
                <div className='bg-gradient-dark absolute top-0 w-full h-80' />

                <div className='container z-10 flex flex-col'>
                    <div className='h-20 flex items-center justify-between'>
                        <h1 className='hidden md:block text-white text-3xl my-auto'>Results</h1>
                        <div className='w-full md:max-w-xl'>
                            <SearchInputField
                                onSearch={(search) => handleQueryParamsUpdate({ search })}
                                initialValue={search}
                                placeholder='Filter results by name'
                                isLoading={isFetching}
                            />
                        </div>
                    </div>
                    <div className='flex-1 relative h-full rounded-md overflow-hidden shadow'>
                        <div className='flex-initial flex flex-row border-b border-gray-200 bg-gray-100 min-h-12 max-h-12'>
                            <div className={clsx('flex-auto px-6', tableHeaderStyle)}>Name</div>
                            <div
                                className={clsx(
                                    'hidden md:block flex-none w-32 text-center border-gray-100 border-l border-r hover:border-l-gray-300 hover:bg-gray-200',
                                    tableHeaderStyle,
                                    {
                                        'bg-gray-200 border-gray-300': status,
                                        'hover:border-r-gray-300': !ordering.includes('started_at'),
                                    },
                                )}
                            >
                                <StatusFilter
                                    label='Status'
                                    selectedValue={status || 'All'}
                                    options={['All', ...Object.values(Status)]}
                                    onChange={(newStatus) =>
                                        handleQueryParamsUpdate({
                                            status: newStatus === 'All' ? '' : newStatus,
                                        })
                                    }
                                />
                            </div>
                            <div
                                className={clsx(
                                    'flex-none w-28 lg:w-56 text-center cursor-pointer border-gray-100 border-l border-r hover:border-r-gray-300 hover:bg-gray-200',
                                    tableHeaderStyle,
                                    {
                                        'hover:border-l-gray-300': !status,
                                        'border-l-gray-300': !status && ordering.includes('started_at'),
                                        'bg-gray-200 border-r-gray-300': ordering.includes('started_at'),
                                    },
                                )}
                                onClick={() => {
                                    handleQueryParamsUpdate({
                                        ordering:
                                            ordering === Order.none
                                                ? Order.startedAtDescending
                                                : ordering === Order.startedAtDescending
                                                  ? Order.startedAtAscending
                                                  : Order.none,
                                    });
                                }}
                            >
                                <span className='inline-flex'>
                                    Started
                                    {ordering === Order.startedAtAscending ? (
                                        <ChevronUpIcon className='h-5 w-5 ml-1' />
                                    ) : ordering === Order.startedAtDescending ? (
                                        <ChevronDownIcon className='h-5 w-5 ml-1' />
                                    ) : (
                                        <ChevronUpDownIcon className='h-5 w-5 ml-1' />
                                    )}
                                </span>
                            </div>
                            <div
                                className={clsx(
                                    'hidden md:block flex-none w-28 lg:w-56 text-center cursor-pointer border-l border-gray-100 hover:bg-gray-200',
                                    tableHeaderStyle,
                                    {
                                        'hover:border-l-gray-300': !ordering.includes('started_at'),
                                        'bg-gray-200 border-l': ordering.includes('finished_at'),
                                    },
                                )}
                                onClick={() => {
                                    handleQueryParamsUpdate({
                                        ordering:
                                            ordering === Order.none
                                                ? Order.finishedAtDescending
                                                : ordering === Order.finishedAtDescending
                                                  ? Order.finishedAtAscending
                                                  : Order.none,
                                    });
                                }}
                            >
                                <span className='inline-flex'>
                                    Finished
                                    {ordering === Order.finishedAtAscending ? (
                                        <ChevronUpIcon className='h-5 w-5 ml-1' />
                                    ) : ordering === Order.finishedAtDescending ? (
                                        <ChevronDownIcon className='h-5 w-5 ml-1' />
                                    ) : (
                                        <ChevronUpDownIcon className='h-5 w-5 ml-1' />
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className='h-full min-h-96 bg-white'>
                            {error ? (
                                <ErrorBanner message={`Failed to load results: ${parseApiErrorMessage(error)}`} />
                            ) : itemCountForInfiniteLoader === 0 ? (
                                <p className='pl-6 py-3'>No results</p>
                            ) : null}
                            <AutoSizer>
                                {({ height, width }) => {
                                    return (
                                        <InfiniteLoader
                                            isItemLoaded={isItemLoaded}
                                            itemCount={itemCountForInfiniteLoader}
                                            loadMoreItems={loadMoreItems}
                                            threshold={PAGE_SIZE}
                                            minimumBatchSize={PAGE_SIZE}
                                        >
                                            {({ onItemsRendered, ref }) => (
                                                <FixedSizeList
                                                    height={height}
                                                    itemCount={itemCountForInfiniteLoader}
                                                    itemSize={70}
                                                    width={width}
                                                    onItemsRendered={onItemsRendered}
                                                    outerRef={listDivRef}
                                                    ref={ref}
                                                >
                                                    {({ index, style }) => (
                                                        <ExperimentRow
                                                            style={style}
                                                            experiment={experimentFromIndex(pageMap, index, PAGE_SIZE)}
                                                            index={index}
                                                        />
                                                    )}
                                                </FixedSizeList>
                                            )}
                                        </InfiniteLoader>
                                    );
                                }}
                            </AutoSizer>
                        </div>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
